import { QuestionCircleOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';

import {
  CompanyFragment,
  LoanBillSettingsType,
  OpenPayoffStatus,
  PortalAutopaySettingsFragment,
  PortalPaymentDueFragment,
  PortalPaymentFragment,
  PortalWorkoutPlanFragment,
} from '@willow/graphql-iso/src/portal';
import { convertNumberToOrdinal, formatDate, phoneNumberFormat } from '@willow/shared-iso';
import {
  getActivePortalDelinquentInterestSetting,
  getNextPaymentDueDate,
  paymentAmountFormat,
  paymentDateFormat,
} from '@willow/shared-web';
import { OverlayTrigger, Tooltip } from '@willow/shared-web/bootstrap';
import { parseGqlIsoDate, PortalPaymentDueSubStatus } from '@willow/types-iso';

import { PortalSelectedLoan } from '../../../App';
import { ActiveWorkoutPlanAlert } from './ActiveWorkoutPlanAlert';
import { AlertWrapper } from './AlertWrapper';
import { OfferedWorkoutPlanAlert } from './OfferedWorkoutPlanAlert';

export interface Props {
  company: CompanyFragment;
  totalAmountDue: number;
  totalAmountDueToReinstateLoan: number;
  totalAmountRemainingFromBorrower: number;
  paymentsDue: PortalPaymentDueFragment[];
  processingAndRecentPayments: PortalPaymentFragment[];
  subStatus?: PortalPaymentDueSubStatus;
  autopaySettings?: PortalAutopaySettingsFragment;
  hasLockedBankAccount: boolean;
  hasInterestReserve: boolean;
  paymentDueStatus: string;
  openPayoffStatus?: OpenPayoffStatus;
  delinquentInterestSettings: PortalSelectedLoan['currentSnapshot']['delinquentInterestSettings'];
  currentBillSettingsType: LoanBillSettingsType;
  activeWorkoutPlan: PortalWorkoutPlanFragment | undefined; // Followup #8543: allow multiple active workout plans
  offeredWorkoutPlans: PortalWorkoutPlanFragment[];
  interestDueAtPayoff: boolean;
  interestAccruedToDate: number;
  chargesAmountRemaining: number;
}

export const PaymentFormAlerts = ({
  company,
  totalAmountDue,
  totalAmountDueToReinstateLoan,
  totalAmountRemainingFromBorrower,
  paymentsDue,
  processingAndRecentPayments,
  subStatus,
  autopaySettings,
  hasLockedBankAccount,
  hasInterestReserve,
  paymentDueStatus,
  openPayoffStatus,
  delinquentInterestSettings,
  currentBillSettingsType,
  activeWorkoutPlan,
  offeredWorkoutPlans,
  interestDueAtPayoff,
  interestAccruedToDate,
  chargesAmountRemaining,
}: Props) => {
  const { isPastDue, paymentFail } = subStatus || {};
  const paymentDate = getNextPaymentDueDate(paymentsDue);
  const hasPendingPayments = processingAndRecentPayments.some((payment) => payment.status === 'PENDING');
  const hasOutstandingBalance = totalAmountRemainingFromBorrower > 0;
  const autopayEnabledByServicer =
    autopaySettings?.actor?.type === 'user' || Boolean(autopaySettings && autopaySettings.type === 'usio');
  const activeDelinquentInterestSetting = delinquentInterestSettings
    ? getActivePortalDelinquentInterestSetting(delinquentInterestSettings)
    : undefined;
  const hasInterestReserveDue = paymentDueStatus !== 'submitted' && totalAmountDue <= 0 && hasInterestReserve;

  // When builder is in charge of the loan, only show that alert message
  if (currentBillSettingsType === LoanBillSettingsType.Builder) {
    return (
      <>
        <AlertWrapper
          title="Do not make a payment unless instructed"
          subtitle="Builder is currently responsible for payments"
          level="ERROR"
        />
        {/* Builder doesn't make portal payments, but we're going to show this alert anyways */}
        <ActiveWorkoutPlanAlert activeWorkoutPlan={activeWorkoutPlan} totalAmountDue={totalAmountDueToReinstateLoan} />
      </>
    );
  }

  const firstDelinquentPaymentDue: Date | undefined = activeDelinquentInterestSetting
    ? DateTime.fromJSDate(parseGqlIsoDate(activeDelinquentInterestSetting.startDate))
        .set({ day: 1 })
        .plus({ months: 1 })
        .toJSDate()
    : undefined;

  return (
    <>
      {interestDueAtPayoff && (
        <AlertWrapper
          title="This loan's interest is due at payoff"
          subtitle={
            chargesAmountRemaining > 0
              ? `${paymentAmountFormat(interestAccruedToDate)} in interest has accrued to date, plus ${paymentAmountFormat(chargesAmountRemaining)} in outstanding fees.`
              : `${paymentAmountFormat(interestAccruedToDate)} in interest has accrued to date.`
          }
          level="INFO"
        />
      )}
      {offeredWorkoutPlans.map((offeredWorkoutPlan) => (
        <OfferedWorkoutPlanAlert key={offeredWorkoutPlan.id} offeredWorkoutPlan={offeredWorkoutPlan} />
      ))}
      <ActiveWorkoutPlanAlert activeWorkoutPlan={activeWorkoutPlan} totalAmountDue={totalAmountDueToReinstateLoan} />

      {hasInterestReserve && totalAmountDue > 0 && (
        <AlertWrapper
          title="Remaining balance due"
          subtitle={`Interest reserve does not cover all of the amount due. Please pay the remaining balance of ${paymentAmountFormat(
            totalAmountDue,
          )}`}
          level="ERROR"
        />
      )}
      {isPastDue &&
        !paymentFail &&
        !interestDueAtPayoff &&
        !['ForbearanceWorkoutPlan', 'FlexModificationWorkoutPlan', 'RepaymentWorkoutPlan'].includes(
          activeWorkoutPlan?.__typename ?? '',
        ) && <AlertWrapper title="Account is past due" subtitle="Please pay immediately to avoid fees" level="ERROR" />}

      {paymentFail && (
        <AlertWrapper
          title="Payment failed to process"
          subtitle={
            <>
              Please try again
              {company?.supportPhone && (
                <>
                  {' '}
                  or call <a href={`tel:${company.supportPhone}`}>{phoneNumberFormat(company.supportPhone)}</a> for help
                </>
              )}
            </>
          }
          level="ERROR"
        />
      )}

      {hasLockedBankAccount && (
        <AlertWrapper
          title="Bank is locked, payment cannot be processed"
          subtitle="Connect a new bank account or contact your bank for support."
          level="ERROR"
        />
      )}

      {hasOutstandingBalance &&
        !isPastDue &&
        !paymentFail &&
        !interestDueAtPayoff &&
        !['ForbearanceWorkoutPlan', 'FlexModificationWorkoutPlan', 'RepaymentWorkoutPlan'].includes(
          activeWorkoutPlan?.__typename ?? '',
        ) && (
          <AlertWrapper
            title="Outstanding balance due"
            subtitle={
              autopaySettings
                ? 'Unpaid balance will be added for auto-payment'
                : `Balance must be paid in full by ${paymentDateFormat(paymentDate, true)}`
            }
            level={isPastDue ? 'ERROR' : 'INFO'}
          />
        )}

      {activeDelinquentInterestSetting && (
        <AlertWrapper
          title="Default interest is charged"
          subtitle={
            <span>
              Daily default interest starts accuring from{' '}
              {formatDate(parseGqlIsoDate(activeDelinquentInterestSetting.startDate), 'M/d/yyyy')}{' '}
              <OverlayTrigger
                placement="top"
                trigger={['hover', 'focus']}
                overlay={
                  <Tooltip>
                    This will be included in the {formatDate(firstDelinquentPaymentDue, 'MMM d, yyyy')} statement and
                    future statements. The amount due changes daily.
                  </Tooltip>
                }
              >
                <QuestionCircleOutlined />
              </OverlayTrigger>
            </span>
          }
          level="ERROR"
        />
      )}

      {openPayoffStatus === OpenPayoffStatus.ActiveOutdated && (
        <AlertWrapper
          title="Payoff statement out of date"
          subtitle="Request a new payoff statement to ensure accuracy"
          level="WARNING"
        />
      )}

      {autopayEnabledByServicer && autopaySettings ? (
        <AlertWrapper
          title="No Action Required: Withdraw Scheduled"
          subtitle={getMessageFromAutopaySettings(autopaySettings)}
          level="INFO"
        />
      ) : (
        hasPendingPayments &&
        totalAmountDue === 0 && (
          <AlertWrapper title="Payment Processing" subtitle="No amount is currently due" level="INFO" />
        )
      )}

      {activeWorkoutPlan && totalAmountDue <= 0 && (
        <AlertWrapper
          title="No payment due"
          subtitle="Your next statement will be available soon with payment details"
          level="INFO"
        />
      )}

      {hasInterestReserveDue && (
        <AlertWrapper
          title="Your payment is scheduled"
          subtitle="Payment will be made from your interest reserve account"
          level="INFO"
        />
      )}
    </>
  );
};

const getMessageFromAutopaySettings = (autopaySettings: PortalAutopaySettingsFragment) => {
  if (autopaySettings.__typename === 'UsioAutopaySettings') {
    const { dayOfTheMonth } = autopaySettings;
    if (dayOfTheMonth) {
      const ordinal = convertNumberToOrdinal(dayOfTheMonth);
      return `Payment will be processed in full automatically on the ${ordinal} of the month`;
    }
  }
  return 'Payment will be processed in full automatically';
};
